const pythonCheatsheet = {
  id: "python",
  title: "Python Cheatsheet",
  description:
    "Complete guide to Python programming from basics to advanced concepts",
  language: "python",
  category: "Programming & Backend",
  level: "All Levels",
  sections: [
    {
      title: "Getting Started",
      items: [
        {
          title: "Hello World",
          content: "Your first Python program",
          code: `# Simple print statement
print("Hello, World!")

# With variables
message = "Hello, World!"
print(message)`,
          note: "Python is known for its simple and readable syntax",
        },
        {
          title: "Variables & Data Types",
          content: "Basic variable declaration and built-in data types",
          code: `# Basic variable assignment
age = 18           # int
name = "John"      # str
height = 1.75      # float
is_student = True  # bool

# Multiple assignment
x, y, z = 1, 2, 3

# Built-in data types
text = "Hello"     # str
number = 42        # int
decimal = 3.14     # float
complex_num = 1j   # complex
items = [1, 2, 3]  # list
items_set = {1, 2} # set
mapping = {"a": 1} # dict
is_true = True     # bool`,
          note: "Python variables don't need explicit type declaration",
        },
      ],
    },
    {
      title: "Basic Operations",
      items: [
        {
          title: "Arithmetic Operations",
          content: "Basic mathematical operations in Python",
          code: `# Basic arithmetic
result = 10 + 30   # Addition (40)
result = 40 - 10   # Subtraction (30)
result = 50 * 5    # Multiplication (250)
result = 16 / 4    # Float Division (4.0)
result = 16 // 4   # Integer Division (4)
result = 25 % 2    # Modulus (1)
result = 5 ** 3    # Exponentiation (125)

# Augmented assignment
counter = 0
counter += 10      # Add and assign
counter -= 5       # Subtract and assign
counter *= 2       # Multiply and assign
counter /= 2       # Divide and assign`,
          note: "Python follows standard mathematical order of operations (PEMDAS)",
        },
        {
          title: "String Operations",
          content: "String manipulation and formatting",
          code: `# String concatenation
first = "Hello"
last = "World"
message = first + " " + last

# String formatting
name = "Alice"
age = 25
# f-strings (Python 3.6+)
print(f"{name} is {age} years old")
# .format() method
print("{} is {} years old".format(name, age))
# %-formatting
print("%s is %d years old" % (name, age))

# String methods
text = "  Hello, World!  "
print(text.strip())      # Remove whitespace
print(text.lower())      # Convert to lowercase
print(text.upper())      # Convert to uppercase
print(text.replace("Hello", "Hi"))  # Replace text`,
          note: "f-strings are the modern way to format strings in Python",
        },
      ],
    },
    {
      title: "Data Structures",
      items: [
        {
          title: "Lists",
          content: "Ordered, mutable sequences",
          code: `# Creating lists
empty_list = []
numbers = [1, 2, 3, 4, 5]
mixed = [1, "hello", 3.14, True]

# List operations
numbers.append(6)       # Add item
numbers.extend([7, 8])  # Add multiple items
numbers.pop()          # Remove last item
numbers.remove(1)      # Remove specific item
numbers.sort()         # Sort list in-place
sorted_nums = sorted(numbers)  # Return new sorted list

# List slicing
first_two = numbers[0:2]    # Get first two items
last_two = numbers[-2:]     # Get last two items
reversed_list = numbers[::-1]  # Reverse list

# List comprehension
squares = [x**2 for x in range(10)]
evens = [x for x in range(10) if x % 2 == 0]`,
          note: "Lists are one of the most versatile data structures in Python",
        },
        {
          title: "Dictionaries",
          content: "Key-value pairs",
          code: `# Creating dictionaries
empty_dict = {}
person = {
    "name": "John",
    "age": 30,
    "city": "New York"
}

# Accessing values
name = person["name"]      # Using key
age = person.get("age")    # Using get() method

# Modifying dictionaries
person["email"] = "john@example.com"  # Add new key-value
person.update({"phone": "123-456-7890"})  # Add multiple
del person["age"]         # Remove key-value

# Dictionary methods
keys = person.keys()      # Get all keys
values = person.values()  # Get all values
items = person.items()    # Get key-value pairs

# Dictionary comprehension
squares = {x: x**2 for x in range(5)}`,
          note: "Dictionaries are optimized for key-based lookups",
        },
      ],
    },
    {
      title: "Control Flow",
      items: [
        {
          title: "Conditional Statements",
          content: "if, elif, and else statements",
          code: `# Basic if statement
if num > 0:
    print("Positive")
elif num < 0:
    print("Negative")
else:
    print("Zero")

# Conditional expressions (ternary operator)
result = "Even" if num % 2 == 0 else "Odd"

# Multiple conditions
if age >= 18 and has_id:
    print("Can vote")
if 0 <= score <= 100:
    print("Valid score")

# is vs ==
a = None
if a is None:  # Use 'is' for None, True, False
    print("a is None")`,
          note: "Python uses indentation to define code blocks",
        },
        {
          title: "Loops",
          content: "for and while loops",
          code: `# For loop with range
for i in range(5):
    print(i)  # Prints 0 to 4

# For loop with list
for item in ["a", "b", "c"]:
    print(item)

# While loop
count = 0
while count < 5:
    print(count)
    count += 1

# Loop control
for i in range(10):
    if i == 3:
        continue  # Skip rest of iteration
    if i == 8:
        break     # Exit loop
    print(i)`,
          note: "Python's for loops iterate over sequences",
        },
      ],
    },
    {
      title: "Functions",
      items: [
        {
          title: "Function Basics",
          content: "Defining and calling functions",
          code: `# Basic function
def greet(name):
    return f"Hello, {name}!"

# Function with default parameters
def greet_with_title(name, title="Mr."):
    return f"Hello, {title} {name}!"

# Multiple return values
def get_coordinates():
    return 3, 4  # Returns tuple

# *args and **kwargs
def flexible_function(*args, **kwargs):
    print(f"Args: {args}")
    print(f"Kwargs: {kwargs}")

# Lambda functions
square = lambda x: x**2`,
          note: "Functions are first-class objects in Python",
        },
        {
          title: "Advanced Functions",
          content: "Decorators and generators",
          code: `# Decorator
def my_decorator(func):
    def wrapper():
        print("Before function")
        func()
        print("After function")
    return wrapper

@my_decorator
def say_hello():
    print("Hello!")

# Generator function
def countdown(n):
    while n > 0:
        yield n
        n -= 1`,
          note: "Decorators modify or enhance functions",
        },
      ],
    },
    {
      title: "Object-Oriented Programming",
      items: [
        {
          title: "Classes and Objects",
          content: "Basic class definition and usage",
          code: `# Basic class
class Dog:
    def __init__(self, name):
        self.name = name
    
    def bark(self):
        return f"{self.name} says woof!"

# Inheritance
class Animal:
    def __init__(self, name):
        self.name = name

class Cat(Animal):
    def meow(self):
        return f"{self.name} says meow!"

# Properties and class methods
class Circle:
    def __init__(self, radius):
        self._radius = radius
    
    @property
    def area(self):
        return 3.14 * self._radius ** 2
    
    @classmethod
    def from_diameter(cls, diameter):
        return cls(diameter / 2)`,
          note: "Python supports multiple inheritance",
        },
      ],
    },
    {
      title: "Error Handling",
      items: [
        {
          title: "Try-Except Blocks",
          content: "Basic error handling",
          code: `# Basic try-except
try:
    result = 10 / 0
except ZeroDivisionError:
    print("Cannot divide by zero!")

# Multiple exceptions
try:
    num = int("abc")
except ValueError:
    print("Invalid number")
except TypeError:
    print("Type error")
else:
    print("No error occurred")
finally:
    print("This always runs")

# Custom exceptions
class CustomError(Exception):
    pass

def validate_age(age):
    if age < 0:
        raise CustomError("Age cannot be negative")`,
          note: "Always catch specific exceptions when possible",
        },
      ],
    },
    {
      title: "File Handling",
      items: [
        {
          title: "File Operations",
          content: "Reading and writing files",
          code: `# Reading files
with open('file.txt', 'r') as f:
    content = f.read()
    lines = f.readlines()

# Writing files
with open('output.txt', 'w') as f:
    f.write('Hello, World!')

# CSV files
import csv
with open('data.csv', 'r') as f:
    reader = csv.reader(f)
    for row in reader:
        print(row)

# JSON files
import json
data = {'name': 'John', 'age': 30}
with open('data.json', 'w') as f:
    json.dump(data, f)`,
          note: "Always use context managers (with) for file operations",
        },
      ],
    },
    {
      title: "Python Generative AI",
      items: [
        {
          title: "Basic AI Integration",
          content: "Using AI libraries in Python",
          code: `# Using OpenAI's GPT
import openai

openai.api_key = 'your-api-key'

response = openai.Completion.create(
    engine="text-davinci-003",
    prompt="Translate to French: Hello, how are you?",
    max_tokens=60
)

# Using Hugging Face Transformers
from transformers import pipeline

# Text generation
generator = pipeline('text-generation')
result = generator("Once upon a time")[0]

# Image generation with DALL-E
image_resp = openai.Image.create(
    prompt="A beautiful sunset over mountains",
    n=1,
    size="1024x1024"
)`,
          note: "Always keep API keys secure and never commit them to version control",
        },
        {
          title: "Machine Learning Basics",
          content: "Basic ML with Python",
          code: `# Using scikit-learn
from sklearn.model_selection import train_test_split
from sklearn.linear_model import LinearRegression
import numpy as np

# Prepare data
X = np.array([[1], [2], [3]])
y = np.array([2, 4, 6])

# Split data
X_train, X_test, y_train, y_test = train_test_split(
    X, y, test_size=0.2
)

# Train model
model = LinearRegression()
model.fit(X_train, y_train)

# Make predictions
predictions = model.predict(X_test)`,
          note: "Start with simple models before moving to complex ones",
        },
      ],
    },
  ],
};

export default pythonCheatsheet;
